import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    X, User, PhoneCall, Mail, MessageSquare,
    Mic, FileText, Upload, ClipboardCheck,
    Calendar, Percent, FileEdit, ExternalLink,
    Phone, MapPin, Search, Send, Clock, ChevronLeft, Paperclip
} from 'lucide-react';
import './PhysicalAppointmentDetails.css';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import ActionOperations from './ActionOperations';
import Alert from '../../../common/Alerts/Alert';
import { serviceInstance } from '../../../axiosinstance';

const PhysicalAppointmentDetails = ({
    task,
    onBack,
    mode = 'view',
    setMode,
    isInline = false
}) => {
    const userId = localStorage.getItem('userId') || '34594';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userList, setUserList] = useState([]);
    const [showAssociatedDropdown, setShowAssociatedDropdown] = useState(false);
    const [associatedSearch, setAssociatedSearch] = useState('');
    const [showExDropdown, setShowExDropdown] = useState(false);
    const [showColDropdown, setShowColDropdown] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [showRemoveSuccess, setShowRemoveSuccess] = useState(false);
    const [alertConfig, setAlertConfig] = useState({
        show: false,
        type: 'success',
        title: '',
        message: '',
        confirmText: 'OK',
        cancelText: 'Cancel',
        onConfirmAction: null
    });

    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const hideAlert = () => {
        if (isMounted.current) {
            setAlertConfig({
                show: false,
                type: 'success',
                title: '',
                message: '',
                confirmText: 'OK',
                cancelText: 'Cancel',
                onConfirmAction: null
            });
        }
    };

    const showInfoAlert = (title, message, type = 'success') => {
        setAlertConfig({
            show: true,
            type,
            title,
            message,
            confirmText: 'OK',
            cancelText: 'Cancel',
            onConfirmAction: null
        });
    };
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [commentData, setCommentData] = useState({ remarks: '', attachment: null, attachmentName: '' });

    // New states for custom Edit UI
    const [salesActivityOptions, setSalesActivityOptions] = useState([]);
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [collaboratorOptions, setCollaboratorOptions] = useState([]);
    const [searchRelatedText, setSearchRelatedText] = useState('');
    const [relatedSuggestions, setRelatedSuggestions] = useState([]);
    const [showRelatedDropdown, setShowRelatedDropdown] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [taskTypeSearch, setTaskTypeSearch] = useState('');
    const [showTaskTypeDropdown, setShowTaskTypeDropdown] = useState(false);
    const whereInputRef = useRef(null);
    const googleAutocompleteRef = useRef(null);

    const [editData, setEditData] = useState({
        PhysicalAppointmentId: (task?.PhysicalAppointmentId || task?.id || '').toString().split('_')[0],
        Title: task?.taskTitle || task?.Title || '',
        Description: task?.description || task?.Description || '',
        Address: task?.address || task?.Address || '',
        StartDate: '',
        StartTime: '',
        EndDate: '',
        EndTime: '',
        GeoEntitySiteId: 0,
        AssignedTo: task?.assignedTo || task?.AssignedTo || '',
        TaskTypeId: task?.TaskId || task?.taskId || '',
        CompleteAddress: '',
        CollaboratorList: task?.collaboratorList || '',
        FieldExecutiveList: '',
        EntityId: task?.EntityId || task?.enquiryId || task?.leadId || '',
        EntityType: 'lead',
        EntityName: task?.name || '',
        Mobile: task?.phone || '',
        EmailId: task?.email || '',
        Latitude: 0,
        Longitude: 0,
        Remarks: task?.remarks || '',
        Outcome: task?.outcomeName || '',
        CompleteAddress: task?.CompleteAddress || '',
        OwnerDisplayName: task?.auDisplayName || 'Mukesh Kumar',
        OriginalEndDate: task?.EndDate || '',
        OriginalStartDate: task?.StartDate || '',
        OriginalCreatedDate: task?.CreatedDate || ''
    });

    const getStatusInfo = (taskData) => {
        const status = (taskData.Status || taskData.taskStatus || '').toLowerCase().trim();
        const statusLabelLocal = (taskData.statusLabel || taskData.StatusLabel || '').toLowerCase().trim();
        const outcomeLocal = (taskData.OutcomeName || taskData.outcomeName || '').toLowerCase().trim();

        const isCompleted = status === 'completed' || status === 'closed' || status === '1291' || status === '1' ||
            statusLabelLocal === 'completed' || statusLabelLocal === 'closed' ||
            outcomeLocal.includes('done') || outcomeLocal.includes('completed');

        const dueDateStr = taskData.EndDate || taskData.StartDate || "";
        const isValDate = dueDateStr && !dueDateStr.startsWith('1900') && dueDateStr !== '0';
        const completedDateStr = taskData.CompletedOn || taskData.completedDate || '';

        let overdueDays = 0;
        let isOverdue = false;
        if (isValDate) {
            const targetDate = new Date(dueDateStr);
            const now = new Date();
            if (!isNaN(targetDate.getTime())) {
                if (!isCompleted && targetDate < now) {
                    isOverdue = true;
                    const diffTime = Math.abs(now - targetDate);
                    overdueDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                } else if (isCompleted && completedDateStr) {
                    const compDate = new Date(completedDateStr);
                    if (!isNaN(compDate.getTime())) {
                        const diffTime = targetDate - compDate;
                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                        if (diffDays > 0) return { label: `COMPLETED ${diffDays} DAY(S) BEFORE DUE DATE`, color: '#28c76f' };
                        if (diffDays < 0) return { label: `COMPLETED ${Math.abs(diffDays)} DAY(S) AFTER DUE DATE`, color: '#28c76f' };
                        return { label: 'COMPLETED ON DUE DATE', color: '#28c76f' };
                    }
                }
            }
        }

        if (isCompleted) return { label: 'COMPLETED', color: '#28c76f' };
        if (isOverdue) return { label: `OVERDUE BY ${overdueDays} DAY(S)`, color: '#ea5455' };

        if (isValDate) {
            const d = new Date(dueDateStr);
            const today = new Date();
            if (d.toDateString() === today.toDateString()) return { label: 'DUE TODAY', color: '#ff9f43' };
        }

        return { label: 'OPEN', color: '#3b82f6' };
    };

    const statusInfo = useMemo(() => getStatusInfo(editData || task || {}), [editData, task, isCompleted]);

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr || dateStr === '0' || dateStr.toString().startsWith('1900')) return '--';

        // If it's already in DD-MMM-YYYY format, return it (to avoid double parsing issues)
        if (typeof dateStr === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}/.test(dateStr)) {
            return dateStr;
        }

        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;

            const day = String(date.getDate()).padStart(2, '0');
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
        } catch (e) {
            return dateStr;
        }
    };

    const formatDateRange = (start, end) => {
        if (!start) return '--';
        return `${formatDateForDisplay(start)} ${end ? ' - ' + formatDateForDisplay(end) : ''}`;
    };

    const [interactionHistory, setInteractionHistory] = useState([]);
    const [interactionHistoryMode, setInteractionHistoryMode] = useState(false);

    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const NEW_BASE_URL_SERVICE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const API_BASE1 = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const PRODUCT_SUGGESTION = `${API_BASE1}/${API_ENDPOINTS.APPOINTMENTS.PRODUCT_SUGGESTION_FOR_FOLLOWUP}`;
    const Follow_up_List = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.APPOINTMENTS.FOLLOWUPSTATUS_LIST}`;
    const HierarchyList = `${API_BASE}/${API_ENDPOINTS.APPOINTMENTS.GET_USER_HIRERARCHY_LIST}`;
    const ADD_LEAD_FOLLOWUP = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.ADD_LEAD_FOLLOWUP}`;
    const GET_INTERACTIONHISTORY = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.GET_INTERACTION_HISTORY}`;
    const Mark_As_Complete = `${NEW_BASE_URL_SERVICE}/${API_ENDPOINTS.APPOINTMENTS.MARK_AS_COMPLETE}`

    const API_BASE2 = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const NEW_ADDED_BASE_URL = (process.env.REACT_APP_BACKEND_BASEURL_FOR_DEVELOPMENT || '').replace(/\/$/, '');
    const API_BASE3 = (process.env.REACT_APP_ASSETS_BASE_URL || '').replace(/\/$/, '');
    const RELATED_TO = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.TASKS.RELATED_TO_SUGGETION}`;
    const url4 = `${API_BASE2}${API_ENDPOINTS.APPOINTMENTS.PHYSICAL_APPOINTMENT_REMOVE}`;
    const url5 = `${API_BASE3}${API_ENDPOINTS.APPOINTMENTS.PHYSICAL_COMMENT_ATTACH_DOC}`;
    const FOR_OWNER = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.APPOINTMENTS.GET_USERLIST_PHYSICALAPP_VIEW}`;
    const FOR_COLLABRATOR = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.APPOINTMENTS.GET_USER_HIRERARCHY_LIST}`;
    const Physical_Appointment_Update = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.APPOINTMENTS.PHYSICAL_APPOINTMENT_UPDATE}`;
    const Physical_APPOINTMENT_Details_ById = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.APPOINTMENTS.GET_USERLIST_PHYSICALAPP_DETAILS_BYID}`;
    const ADD_COMMENT = `${NEW_BASE_URL_SERVICE}${API_ENDPOINTS.TASKS.ADD_COMMENT}`;
    const GET_TASK_COMMENTS = `${NEW_ADDED_BASE_URL}${API_ENDPOINTS.COMMON.GET_COMMENTS}`;
    const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;

    const renderAvatar = (name, url = null, size = 'sm') => {
        const getAvatarSize = (s) => {
            if (s === 'lg') return '60px';
            if (s === 'xs') return '32px';
            return '40px';
        };

        const getFontSize = (s) => {
            if (s === 'lg') return '24px';
            if (s === 'xs') return '12px';
            return '14px';
        };

        const avatarSize = getAvatarSize(size);
        const getInitials = (n) => {
            if (!n) return '';
            const cleanName = String(n).trim();
            if (/^\+?\d+/.test(cleanName)) {
                return cleanName.replace('+', '').substring(0, 2);
            }
            const parts = cleanName.split(' ').filter(p => p.length > 0);
            if (parts.length === 1) return parts[0][0].toUpperCase();
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        };
        const initials = getInitials(name);



        return (
            <div className={`avatar-initials-circle ${size}`} style={{
                backgroundColor: '#f1f5f9',
                position: 'relative',
                width: avatarSize,
                height: avatarSize,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                fontWeight: '700',
                fontSize: getFontSize(size),
                border: '1px solid #e2e8f0'
            }}>
                {initials}
            </div>
        );
    };

    const fetchAppointmentDetails = async (id) => {
        const parseAPIDate = (str) => {
            if (!str) return { date: '', time: '' };
            try {
                // If already in DD-MMM-YYYY format, we need to convert it to YYYY-MM-DD for input[type="date"]
                let d;
                if (typeof str === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}/.test(str)) {
                    const parts = str.split(' ')[0].split('-');
                    const day = parts[0];
                    const monthStr = parts[1];
                    const year = parts[2];
                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const month = String(months.indexOf(monthStr) + 1).padStart(2, '0');
                    d = new Date(`${year}-${month}-${day}`);
                } else {
                    d = new Date(str);
                }

                if (isNaN(d.getTime())) return { date: '', time: '' };
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');

                // For time, if the original string has HH:mm:ss, extract it
                let hh = String(d.getHours()).padStart(2, '0');
                let min = String(d.getMinutes()).padStart(2, '0');
                if (typeof str === 'string' && str.includes(':')) {
                    const timePart = str.split(' ')[1];
                    if (timePart) {
                        const tParts = timePart.split(':');
                        hh = tParts[0] || hh;
                        min = tParts[1] || min;
                    }
                }

                return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
            } catch (e) { return { date: '', time: '' }; }
        };

        try {
            const response = await serviceInstance.post(Physical_APPOINTMENT_Details_ById, {
                Token: TOKEN || "-2295521862261168",
                Details: {
                    PhysicalAppointmentId: Number(id),
                    UserId: Number(userId)
                }
            });
            const result = response.data;
            if (result.d || result.Details) {
                const rawData = result.Details || result.d;
                const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
                const appt = Array.isArray(data) ? data[0] : data;

                const startParsed = parseAPIDate(appt.StartDate);
                const endParsed = parseAPIDate(appt.EndDate);

                const parseUserList = (list) => {
                    if (!list) return '';
                    if (typeof list === 'string') {
                        if (list.startsWith('[') && list.endsWith(']')) {
                            try {
                                const parsed = JSON.parse(list);
                                return parsed.map(u => u.fuUserId || u.UserId || u.Id).filter(Boolean).join(',');
                            } catch (e) { return list; }
                        }
                        return list;
                    }
                    if (Array.isArray(list)) {
                        return list.map(u => u.fuUserId || u.UserId || u.Id).filter(Boolean).join(',');
                    }
                    return '';
                };

                if (isMounted.current) {
                    const collabList = parseUserList(appt.Collaborators || appt.CollaboratorList);
                    const execList = parseUserList(appt.FieldExecutives || appt.FieldExecutiveList);

                    setEditData(prev => ({
                        ...prev,
                        PhysicalAppointmentId: appt.PhysicalAppointmentId || '',
                        Title: appt.Title || '',
                        Description: appt.Description || '',
                        Address: appt.Address || appt.smAddress || '',
                        StartDate: startParsed.date,
                        StartTime: startParsed.time,
                        EndDate: endParsed.date,
                        Time: endParsed.time,
                        AssignedTo: appt.AssignedTo || appt.auUserId || '',
                        TaskTypeId: appt.TaskTypeId || appt.tmId || '',
                        CompleteAddress: appt.CompleteAddress || appt.smMapAddress || '',
                        CollaboratorList: collabList,
                        FieldExecutiveList: execList,
                        EntityId: appt.EntityId || appt.geEntityId || '',
                        EntityType: appt.EntityType || appt.geGeoEntityType || 'lead',
                        EntityName: appt.EntityName || appt.geGeoEntityName || '',
                        Mobile: (appt.Mobile || appt.geMobile || '').replace(/^,+|,+$/g, '').replace(/,,+/g, ','),
                        EmailId: (appt.EmailId || appt.geEmailId || '').replace(/^,+|,+$/g, '').replace(/,,+/g, ','),
                        Outcome: appt.otOutcomeName || appt.Outcome || '',
                        OwnerDisplayName: appt.auDisplayName || appt.cuDisplayName || '',
                        OriginalEndDate: appt.EndDate || '',
                        OriginalStartDate: appt.StartDate || '',
                        OriginalCreatedDate: appt.CreatedDate || appt.CreatedOn || '',
                        CompletedOn: appt.CompletedOn || ''
                    }));
                    setSearchRelatedText(appt.EntityName || '');
                    setIsCompleted(appt.Status === 'Completed' || appt.Status === '1291');
                }
            }
        } catch (error) { console.error("Error fetching appointment details:", error); }
    };

    const fetchInteractionHistory = async (silent = false) => {
        try {
            const id = task.PhysicalAppointmentId || task.id;
            const payload = {
                Token: TOKEN || "-2295521862261168",
                Details: {
                    Id: parseInt(id),
                    Mode: "P"
                }
            };

            const response = await fetch(GET_TASK_COMMENTS, {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                if (result.Details) {
                    if (isMounted.current) {
                        setInteractionHistory(Array.isArray(result.Details) ? result.Details : []);
                        if (!silent) setInteractionHistoryMode(true);
                    }
                }
            }
        } catch (error) { console.error("Error fetching interaction history:", error); }
    };

    useEffect(() => {
        if (task?.id || task?.PhysicalAppointmentId) {
            const id = task.PhysicalAppointmentId || task.id;
            fetchAppointmentDetails(id);
            fetchInteractionHistory(true);
        }
    }, [task?.id, task?.PhysicalAppointmentId]);

    const handleCommentSubmit = async () => {
        if (!commentData.remarks.trim()) return;
        await handleAddComment();
        fetchInteractionHistory(true);
    };

    const handleAddComment = async () => {
        if (!commentData.remarks.trim()) { alert("Please enter a comment"); return; }
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            const detailsObj = {
                Token: TOKEN || "-2295521862261168",
                taskAttachment: {
                    Remarks: commentData.remarks,
                    CreatedBy: parseInt(userId),
                    TaskId: parseInt(editData.PhysicalAppointmentId || task.PhysicalAppointmentId || task.id),
                    ParentId: parseInt(userId),
                    Mode: "C"
                }
            };

            formData.append('details', JSON.stringify(detailsObj));
            formData.append('file', commentData.attachment || '');

            const response = await fetch(ADD_COMMENT, {
                method: 'POST',
                body: formData
            });

            if (response.ok && isMounted.current) {
                setCommentData({ remarks: '', attachment: null, attachmentName: '' });
                setShowCommentModal(false);
                fetchInteractionHistory(true);
            }
        } catch (error) {
            console.error("Error adding comment:", error);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    const handleRemove = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                Mode: "PD",
                PhysicalAppointmentId: String(editData.PhysicalAppointmentId).split('_')[0],
                CreatedBy: String(userId)
            };
            const response = await fetch(url4, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const resData = await response.json();
            if (response.ok && (resData.Status === 1 || resData.Status === 200)) {
                if (isMounted.current) {
                    setShowRemoveConfirm(false);
                    setShowRemoveSuccess(true);
                }
            } else {
                if (isMounted.current) alert(resData.Message || "Failed to remove appointment.");
            }
        } catch (e) {
            console.error(e);
            alert("An error occurred while removing.");
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    const handleSaveEdit = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                Token: TOKEN,
                LoggedUserId: userId,
                Details: {
                    Mode: "PU",
                    PhysicalAppointmentId: (editData.PhysicalAppointmentId?.toString() || "").split('_')[0],
                    Address: editData.Address,
                    AssignedTo: editData.AssignedTo || userId,
                    CollaboratorList: editData.CollaboratorList || "",
                    CompleteAddress: editData.CompleteAddress,
                    CreatedBy: parseInt(userId),
                    Description: editData.Description,
                    EndDate: `${editData.EndDate} ${editData.EndTime}`,
                    EntityId: editData.EntityId?.toString(),
                    EntityName: editData.EntityName,
                    EntityType: editData.EntityType,
                    StartDate: `${editData.StartDate} ${editData.StartTime}`,
                    TaskTypeId: parseInt(editData.TaskTypeId) || 505,
                    Title: editData.Title,
                }
            };

            const response = await fetch(Physical_Appointment_Update, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                if (isMounted.current) {
                    if (result.Status === 1 || result.Status === 200) {
                        setShowSuccessModal(true);
                    } else {
                        alert(result.Message || "Failed to update appointment");
                    }
                }
            }
        } catch (error) {
            console.error("Error saving edit:", error);
        } finally {
            if (isMounted.current) setIsSubmitting(false);
        }
    };

    const fetchRelatedToSuggestions = async (text, type) => {
        if (!text || text.length < 2) {
            setRelatedSuggestions([]);
            return;
        }
        try {
            const response = await fetch(RELATED_TO, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Token: TOKEN,
                    Details: { UserId: parseInt(userId), FilterText: text, TaskType: type.toLowerCase() }
                })
            });
            const result = await response.json();
            const data = result.Details || result.d || [];
            if (isMounted.current) {
                setRelatedSuggestions(typeof data === 'string' ? JSON.parse(data) : data);
                setShowRelatedDropdown(true);
            }
        } catch (err) { console.error(err); }
    };

    const renderHeader = () => (
        <div className="task-details-header" style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 24px',
            background: '#fff',
            borderBottom: '1px solid #f1f5f9',
            height: '60px'
        }}>
            {!isInline && (
                <button
                    className="back-btn-premium"
                    onClick={onBack}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        padding: '6px 14px',
                        borderRadius: '8px',
                        color: '#475569',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    <ChevronLeft size={16} />
                    Back to Physical Appointment
                </button>
            )}
        </div>
    );

    const renderPanelContent = () => (
        <div className={`task-details-main-content-container ${isInline ? 'is-inline' : ''}`} style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
            {!isInline && renderHeader()}
            <div className="task-details-content thin-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: isInline ? '15px 25px' : '25px' }}>
                {interactionHistoryMode ? (
                    <div className="interaction-history-premium-container">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>History Log</h3>
                            <button className="premium-action-btn" onClick={() => setInteractionHistoryMode(false)} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>Close History</button>
                        </div>
                        <div className="history-list">
                            {interactionHistory.length > 0 ? (
                                interactionHistory.map((item, idx) => (
                                    <div key={idx} className="interaction-card-premium" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', marginBottom: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                {renderAvatar(item.CreatedBy || 'User', null, 'xs')}
                                                <span style={{ fontWeight: 'bold' }}>{item.CreatedBy}</span>
                                            </div>
                                            <span style={{ fontSize: '12px', color: '#8898aa' }}>{item.CreatedOn}</span>
                                        </div>
                                        <p style={{ margin: '0', fontSize: '14px', color: '#525f7f' }}>{item.Remarks}</p>
                                    </div>
                                ))
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#8898aa' }}>No history found.</div>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        {(mode === 'edit' || ['followup', 'send-sms', 'send-mail', 'send-voice', 'task', 'appointment', 'deal', 'webform', 'notes', 'upload', 'fill-webform', 'add-notes'].includes(mode)) && (
                            <ActionOperations
                                task={{
                                    ...task,
                                    taskTitle: editData.Title || task.taskTitle || task.Title || '',
                                    Title: editData.Title || task.Title || '',
                                    description: editData.Description || task.description || task.Description || '',
                                    Description: editData.Description || task.Description || '',
                                    remarks: editData.Remarks || task.remarks || task.Remarks || '',
                                    Remarks: editData.Remarks || task.Remarks || '',
                                    startDate: editData.OriginalStartDate || task.startDate || task.StartDate || '',
                                    StartDate: editData.OriginalStartDate || task.StartDate || '',
                                    endDate: editData.OriginalEndDate || task.endDate || task.EndDate || '',
                                    EndDate: editData.OriginalEndDate || task.EndDate || '',
                                    address: editData.Address || task.address || task.Address || '',
                                    Address: editData.Address || task.Address || '',
                                    CompleteAddress: editData.CompleteAddress || task.CompleteAddress || '',
                                    taskId: editData.TaskTypeId || task.taskId || task.TaskId || '',
                                    TaskId: editData.TaskTypeId || task.TaskId || '',
                                    assignedTo: editData.AssignedTo || task.assignedTo || task.AssignedTo || '',
                                    AssignedTo: editData.AssignedTo || task.AssignedTo || '',
                                    collaboratorList: editData.CollaboratorList || task.collaboratorList || '',
                                    CollaboratorUserIds: editData.CollaboratorList || task.CollaboratorUserIds || '',
                                    entityName: editData.EntityName || task.entityName || task.name || '',
                                    entityId: editData.EntityId || task.EntityId || task.leadId || task.enquiryId || task.id,
                                    entityType: editData.EntityType || task.entityType || 'lead',
                                    leadId: editData.EntityId || task.EntityId || task.leadId || task.enquiryId || task.id,
                                    enquiryId: editData.EntityId || task.EntityId || task.enquiryId || task.leadId || task.id,
                                    Latitude: editData.Latitude || task.Latitude || '',
                                    Longitude: editData.Longitude || task.Longitude || '',
                                    Mobile: editData.Mobile || task.Mobile || '',
                                    EmailId: editData.EmailId || task.EmailId || '',
                                    actionType: (mode === 'edit') ? (task.isNew ? (task.actionType || 'appointment') : 'appointment') : mode.replace('send-', '').replace('add-', '').replace('fill-', '')
                                }}
                                onBack={() => {
                                    onBack(); // Close drawer/panel and return to main list
                                }}
                                leadId={editData.EntityId || task.EntityId || task.enquiryId || task.leadId || task.id}
                            />
                        )}
                    </>
                )}
                {mode === 'view' && !interactionHistoryMode && (
                    <div className="premium-appointment-details-container">
                        <div className="task-details-main-card">
                            <div className="task-details-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h1 className="task-details-main-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: '0' }}>
                                    {editData.Title || task.taskTitle}
                                </h1>
                                <div className="status-badge-luxury" style={{
                                    backgroundColor: statusInfo.color,
                                    color: '#fff',
                                    padding: '6px 16px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    textTransform: 'uppercase'
                                }}>
                                    {statusInfo.label}
                                </div>
                            </div>

                            <div className="related-to-premium-section" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                                {renderAvatar(editData.EntityName || task.name || 'User', editData.ProfileImgFileName || editData.Image || task.ProfileImgFileName || task.Image, 'sm')}
                                <div className="related-text-info">
                                    <span className="related-label" style={{ fontSize: '11px', fontWeight: 'bold', color: '#8898aa', display: 'block', textTransform: 'uppercase' }}>RELATED TO:</span>
                                    <div className="related-link-row" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <span className="related-name" style={{ fontSize: '14px', color: '#1e293b', fontWeight: '700' }}>{editData.EntityName || task.name || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="task-metadata-details-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '16px 40px',
                                padding: '20px',
                                background: '#ffffff',
                                border: '1px solid #edf2f7',
                                borderRadius: '12px',
                                marginBottom: '20px'
                            }}>
                                <div className="metadata-item">
                                    <div style={{ fontSize: '11px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>Due Date</div>
                                    <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: '600' }}>
                                        {formatDateForDisplay(editData.OriginalEndDate || editData.EndDate || task.EndDate)}
                                    </div>
                                </div>
                                <div className="metadata-item">
                                    <div style={{ fontSize: '11px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>Owner</div>
                                    <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: '600' }}>{editData.OwnerDisplayName || '--'}</div>
                                </div>
                                <div className="metadata-item">
                                    <div style={{ fontSize: '11px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>Completed On</div>
                                    <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: '600' }}>
                                        {formatDateForDisplay(editData.CompletedOn || '--')}
                                    </div>
                                </div>
                                <div className="metadata-item">
                                    <div style={{ fontSize: '11px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>Outcomes</div>
                                    <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: '600' }}>{editData.Outcome || '--'}</div>
                                </div>
                                <div className="metadata-item">
                                    <div style={{ fontSize: '11px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>Collaborators</div>
                                    <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: '600' }}>{editData.CollaboratorList || '--'}</div>
                                </div>
                                <div className="metadata-item">
                                    <div style={{ fontSize: '11px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>Created On</div>
                                    <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: '600' }}>
                                        {formatDateForDisplay(editData.OriginalCreatedDate || task.CreatedDate || task.createdDate)}
                                    </div>
                                </div>
                            </div>

                            <div className="description-premium-section">
                                <div className="description-content-box" style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '24px' }}>
                                    <div className="description-label-row" style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase' }}>
                                        <FileText size={16} />
                                        <span>DESCRIPTION:</span>
                                    </div>
                                    <p style={{ margin: '0', fontSize: '14px', color: '#334155', lineHeight: '1.6' }}>{editData.Description || 'No description provided.'}</p>

                                    {editData.CompleteAddress && (
                                        <div className="address-sub-section" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f1f5f9' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                                <MapPin size={18} style={{ color: '#64748b', marginTop: '2px' }} />
                                                <span style={{ fontSize: '14px', color: '#475569' }}>{editData.CompleteAddress}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="comments-premium-tab-section" style={{ marginTop: '40px' }}>
                                <div className="comments-tab-header" style={{ marginBottom: '20px' }}>
                                    <span className="active-tab" style={{ borderBottom: '3px solid #5e72e4', paddingBottom: '10px', display: 'inline-block', fontWeight: 'bold', color: '#5e72e4', fontSize: '16px' }}>Comments</span>
                                </div>

                                <div className="comments-list-container" style={{ minHeight: '100px', marginBottom: '20px' }}>
                                    {interactionHistory.length > 0 ? (
                                        interactionHistory.map((item, idx) => (
                                            <div key={idx} className="comment-item-premium" style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                                                <div className="comment-avatar" style={{ flexShrink: 0 }}>
                                                    {renderAvatar(item.display_name || 'User', item.Image || item.ProfileImg || item.ProfileImgFileName, 'xs')}
                                                </div>
                                                <div className="comment-content" style={{ flex: 1 }}>
                                                    <div className="comment-header-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                                        <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{item.display_name}</span>
                                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>{item.created_on}</span>
                                                    </div>
                                                    <p style={{ margin: '0', fontSize: '14px', color: '#475569', lineHeight: '1.4' }}>{item.remarks}</p>
                                                    {item.attachments && item.attachments.length > 0 && item.attachments.map((att, attIdx) => (
                                                        att.FileName && (
                                                            <div key={attIdx} style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                <Paperclip size={12} style={{ color: '#2563eb' }} />
                                                                <a href={att.FileUrl} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}>{att.FileName}</a>
                                                            </div>
                                                        )
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '14px' }}>No comments yet.</div>
                                    )}
                                </div>

                                <div className="modern-comment-input-wrapper" style={{
                                    background: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    padding: '10px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}>
                                    <input
                                        type="text"
                                        placeholder="Enter comment..."
                                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: '14px', color: '#1e293b' }}
                                        value={commentData.remarks}
                                        onChange={(e) => setCommentData(prev => ({ ...prev, remarks: e.target.value }))}
                                        onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#94a3b8' }}>
                                            <input
                                                type="file"
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) setCommentData(prev => ({ ...prev, attachment: file, attachmentName: file.name }));
                                                }}
                                            />
                                            <Paperclip size={20} />
                                        </label>
                                        <button
                                            onClick={handleCommentSubmit}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#22c55e',
                                                fontWeight: '700',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                padding: '4px 8px'
                                            }}
                                        >
                                            Send
                                        </button>
                                    </div>
                                </div>
                                {commentData.attachmentName && (
                                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px', padding: '0 16px' }}>
                                        <Paperclip size={12} /> {commentData.attachmentName}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    if (!task) return null;

    return (
        <>
            {renderPanelContent()}

            {showSuccessModal && (
                <div className="success-modal-overlay">
                    <div className="success-modal">
                        <div className="success-icon-wrapper">
                            <div className="success-icon-circle">✓</div>
                        </div>
                        <h2 className="modal-title">Success</h2>
                        <p className="modal-message">Appointment data saved successfully</p>
                        <button className="modal-ok-btn" onClick={() => { setShowSuccessModal(false); if (!isInline) onBack(); }}>OK</button>
                    </div>
                </div>
            )}

            {showRemoveConfirm && (
                <div className="success-modal-overlay">
                    <div className="success-modal remove-confirm-modal">
                        <div className="confirm-icon-wrapper">
                            <div className="confirm-icon-circle">!</div>
                        </div>
                        <h1 className="confirm-title">Are you sure?</h1>
                        <p className="confirm-message">Do you want to remove selected appointment!</p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <button className="cancel-btn-luxury" onClick={() => setShowRemoveConfirm(false)}>No, cancel it!</button>
                            <button className="confirm-btn-luxury" onClick={handleRemove}>Yes, I am sure!</button>
                        </div>
                    </div>
                </div>
            )}

            {showRemoveSuccess && (
                <div className="success-modal-overlay">
                    <div className="success-modal">
                        <div className="success-icon-wrapper">
                            <div className="success-icon-circle">✓</div>
                        </div>
                        <h2 className="modal-title">Success</h2>
                        <p className="modal-message">Appointment removed successfully</p>
                        <button className="modal-ok-btn" onClick={() => { setShowRemoveSuccess(false); if (!isInline) onBack(); }}>OK</button>
                    </div>
                </div>
            )}

            {showCommentModal && (
                <div className="success-modal-overlay">
                    <div className="success-modal comment-modal">
                        <div className="modal-header-luxury"><h2>Add Comment</h2><button className="close-x" onClick={() => setShowCommentModal(false)}>×</button></div>
                        <div className="modal-body-luxury">
                            <textarea className="luxury-input" rows="4" value={commentData.remarks} onChange={(e) => setCommentData({ ...commentData, remarks: e.target.value })} placeholder="Enter your comment here..."></textarea>
                        </div>
                        <div className="modal-footer-luxury">
                            <button className="cancel-btn-luxury" onClick={() => setShowCommentModal(false)}>Close</button>
                            <button className="confirm-btn-luxury" onClick={handleAddComment}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PhysicalAppointmentDetails;
